import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Copy, Share2, MessageSquare, Mail, Link as LinkIcon, Check } from 'lucide-react';

/**
 * Big, warm invite sheet. Replaces the tiny code pill in the header.
 * Multiple ways to share so the user picks whatever's fastest for their people.
 */
const InviteFriendsDialog = ({ open, onOpenChange, circle, userName }) => {
  const [copied, setCopied] = useState(null);
  const name = userName || 'Someone';
  const circleName = circle?.name || 'their care circle';
  const code = circle?.invite_code || circle?.code || '';
  const link = code ? `https://circleupsquad.com/auth?code=${code}` : 'https://circleupsquad.com';
  const message = `Hi! ${name} invited you to their ${circleName} on CircleUpSquad — a private space where we coordinate meals, rides, and support. Tap to join:\n\n${link}`;

  const copy = async (text, kind) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      toast.success(kind === 'code' ? 'Code copied' : 'Invite message copied — paste it anywhere');
      setTimeout(() => setCopied(null), 2200);
    } catch {
      toast.error('Could not copy — try long-pressing to copy manually');
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Join ${circleName}`, text: message, url: link });
      } catch (e) { if (e.name !== 'AbortError') copy(message, 'msg'); }
    } else {
      copy(message, 'msg');
    }
  };

  const smsHref = `sms:?&body=${encodeURIComponent(message)}`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent(`Join ${circleName} on CircleUpSquad`)}&body=${encodeURIComponent(message)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="invite-friends-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>Invite people to your circle</DialogTitle>
          <p className="text-sm text-muted-foreground">The more people in your corner, the lighter the load.</p>
        </DialogHeader>

        {/* Message preview */}
        <div className="bg-muted/50 rounded-xl p-4 border border-border" data-testid="invite-message-preview">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">What they'll see</p>
          <p className="text-sm text-foreground whitespace-pre-line leading-relaxed">{message}</p>
        </div>

        {/* Primary share actions */}
        <div className="space-y-2">
          <Button onClick={shareNative} className="btn-primary w-full text-base py-6" data-testid="invite-share-btn">
            <Share2 className="w-5 h-5 mr-2" /> Share invite
          </Button>

          <div className="grid grid-cols-3 gap-2">
            <a href={smsHref} data-testid="invite-sms-btn"
               className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl border border-border hover:border-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 transition-colors text-foreground">
              <MessageSquare className="w-5 h-5 text-cyan-600" />
              <span className="text-xs font-medium">Text</span>
            </a>
            <a href={whatsappHref} target="_blank" rel="noreferrer" data-testid="invite-whatsapp-btn"
               className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl border border-border hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors text-foreground">
              <svg className="w-5 h-5 text-emerald-600" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.15-1.8-.9-2.1-1-.28-.1-.48-.15-.7.15-.2.3-.78 1-.96 1.2-.18.2-.35.22-.65.08-.3-.15-1.26-.47-2.4-1.5-.9-.8-1.5-1.78-1.66-2.08-.18-.3-.02-.45.13-.6.13-.13.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.7-1.68-.95-2.3-.25-.6-.5-.52-.7-.53h-.6c-.2 0-.53.08-.8.38-.28.3-1.05 1.03-1.05 2.5s1.08 2.9 1.23 3.1c.15.2 2.1 3.2 5.1 4.5.7.3 1.26.48 1.7.62.7.22 1.35.19 1.86.12.57-.08 1.75-.72 2-1.4.25-.7.25-1.28.17-1.4-.08-.13-.28-.2-.58-.35zM12.05 21.5h-.02c-1.75 0-3.5-.47-5-1.36l-.36-.2-3.7.96.99-3.6-.23-.37c-.98-1.56-1.5-3.35-1.5-5.2 0-5.4 4.4-9.78 9.83-9.78 2.6 0 5.06 1.03 6.9 2.88 1.85 1.85 2.87 4.3 2.87 6.9 0 5.4-4.4 9.78-9.8 9.78zm8.36-18.17C18.15 1.08 15.2 0 12.05 0 5.5 0 .18 5.3.18 11.83c0 2.1.55 4.14 1.6 5.94L0 24l6.34-1.65c1.75.95 3.72 1.45 5.7 1.45h.02c6.55 0 11.87-5.3 11.87-11.83 0-3.16-1.24-6.13-3.5-8.37z"/></svg>
              <span className="text-xs font-medium">WhatsApp</span>
            </a>
            <a href={emailHref} data-testid="invite-email-btn"
               className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl border border-border hover:border-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/30 transition-colors text-foreground">
              <Mail className="w-5 h-5 text-violet-600" />
              <span className="text-xs font-medium">Email</span>
            </a>
          </div>
        </div>

        {/* Secondary — copy variants */}
        <div className="space-y-2 pt-2 border-t border-border">
          <button onClick={() => copy(message, 'msg')} data-testid="invite-copy-msg-btn"
                  className="w-full flex items-center justify-between p-3 rounded-xl border border-border hover:bg-muted/50 transition-colors text-left">
            <div>
              <p className="text-sm font-semibold text-foreground">Copy full invite message</p>
              <p className="text-xs text-muted-foreground">Paste into DMs, group texts, wherever</p>
            </div>
            {copied === 'msg' ? <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <Copy className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
          </button>
          <button onClick={() => copy(link, 'link')} data-testid="invite-copy-link-btn"
                  className="w-full flex items-center justify-between p-3 rounded-xl border border-border hover:bg-muted/50 transition-colors text-left">
            <div>
              <p className="text-sm font-semibold text-foreground">Copy join link</p>
              <p className="text-xs text-muted-foreground font-mono truncate max-w-[240px]">{link}</p>
            </div>
            {copied === 'link' ? <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <LinkIcon className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
          </button>
          {code && (
            <button onClick={() => copy(code, 'code')} data-testid="invite-copy-code-btn"
                    className="w-full flex items-center justify-between p-3 rounded-xl border border-border hover:bg-muted/50 transition-colors text-left">
              <div>
                <p className="text-sm font-semibold text-foreground">Just the code</p>
                <p className="text-xs text-muted-foreground font-mono tracking-widest">{code}</p>
              </div>
              {copied === 'code' ? <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <Copy className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteFriendsDialog;
